import { useQuery } from "@tanstack/react-query";
import LoadingPulse from "../loaders/loadingPulse";
import { displayPercent } from "../../utils";
import { useEmbeddyCrockerApiClient } from "../../hooks/useApiClient";
import { CLASSIFICATION_THRESHOLD } from "../../utils/aiSearchUtil";
import {ImageMetadataComponentProps} from "../gallery/types";
import {isPaginatedResponse} from "../../apiUtils";
import {
  ImagePredictionWithImageAndClassificationResponse,
  Page_ImagePredictionWithImageAndClassificationResponse_
} from "../../embeddyCrockerApiClient";


const AIImageMetadataContent = ({ imageSrc }: ImageMetadataComponentProps) => {
  const api = useEmbeddyCrockerApiClient();
  const { data: predictions, isLoading: classificationsAreLoading } =
    useQuery({
      queryKey: ["ai-image-search-classifications", imageSrc],
      queryFn: async () =>
        api.prediction
          .getImagePredictions({ url: imageSrc, sortBy: "probability" })
          .then((m) => isPaginatedResponse<Page_ImagePredictionWithImageAndClassificationResponse_>(m) ? m.items : []),
      throwOnError: true,
      refetchOnWindowFocus: false,
    });

  const { data: metadata, isLoading: imageMetadataIsLoading } = useQuery({
    queryKey: ["ai-image-search-metadata", imageSrc],
    queryFn: async () =>
      api.imageEmbedding
        .getImageMetadata({ url: imageSrc })
        .then((m) => m.items[0]),
    throwOnError: true,
    refetchOnWindowFocus: false,
  });

  const likelyPredictions: ImagePredictionWithImageAndClassificationResponse[] = (predictions ?? []).filter(
    (c) => c.probability > CLASSIFICATION_THRESHOLD
  );

  return (
    <>
      <div className="metadata-section min-w-[200px]">
        <h4 className="text-lg font-semibold text-blue-200">Image Metadata</h4>
        <div className="overflow-y-scroll">
          {imageMetadataIsLoading ? (
              <LoadingPulse  />
          ) : (
            <>
              <p className="text-sm">
                <strong>Source:</strong> {metadata?.source}
              </p>
              <p className="text-sm">
                <strong>Image Date:</strong>{" "}
                {metadata?.extra?.image_dt
                  ? new Date(metadata.extra.image_dt).toLocaleString()
                  : "N/A"}
              </p>


              <h5 className="text-md font-semibold text-blue-200">
                Annotations
              </h5>
              <p className="text-sm">No Annotations Found</p>
            </>
          )}

        </div>
      </div>



      <div className="classifications-section min-w-[200px]">
        <h4 className="text-lg font-semibold text-blue-200">
          Similar Classifications
        </h4>
        <div className="h-[200px] overflow-y-scroll">
          {classificationsAreLoading ? (
              <LoadingPulse />
          ) : likelyPredictions?.length > 0 ? (
            <ul className="list-disc pl-5">
              {likelyPredictions.map(({probability, classification}) => (
                <li key={`${classification.type}-${classification.name}`} className="text-sm">
                  <strong>
                    {classification.type.charAt(0).toUpperCase() +
                      classification.type.slice(1)}
                    :
                  </strong>{" "}
                  {classification.name} - Similarity:{" "}
                  {displayPercent(probability)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm">
              No classifications found with {">"}
              {displayPercent(CLASSIFICATION_THRESHOLD)} Probability
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default AIImageMetadataContent;
